import { Page } from '@app/components/page/Page';
import { PageSkeleton } from '@app/components/page/header/skeleton/PageSkeleton';
import { PageLoader } from '@app/components/page/header/skeleton/PageLoader';
import { ComposeIcon } from '@app/modules/compose/components/ComposeIcon';
import { useCompositionQuery } from '@app/modules/compose/hooks/useCompositionQuery';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { BreadcrumbItem } from '@mi-front/ui';
import { defineMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useComposition } from '../view/hooks/useComposition';
import { ComposeEditForm } from './ComposeEditForm';

const composeEditPageTitleMessage = defineMessage({
  id: 'compose.editPage.title',
  defaultMessage: 'Editar composición',
});

const composeListHubLabelMessage = defineMessage({
  id: 'compose.listScreen.hubLabel',
  defaultMessage: 'Compose',
});

/**
 * Full-page form to edit a composition (name only).
 */
function ComposeEditPageContent() {
  const { composition, station } = useComposition();
  const { $t } = useIntl();

  const title = $t(composeEditPageTitleMessage);
  const listPath = station
    ? composeRouteGenerator.listForStationSlug(station.slug)
    : composeRouteGenerator.view(composition.id);
  const viewPath = composeRouteGenerator.edits(composition.id);

  const breadcrumbItems: readonly BreadcrumbItem[] = [
    {
      icon: <ComposeIcon fontSize="inherit" />,
      content: $t(composeListHubLabelMessage),
      href: listPath,
    },
    ...(station
      ? [
          {
            content: station.name,
            href: listPath,
          },
        ]
      : []),
    {
      content: composition.name,
      href: viewPath,
    },
    {
      content: title,
      active: true,
    },
  ];

  return (
    <Page htmlTitle={`${title} | ${composition.name}`}>
      <Page.Header
        icon={<ComposeIcon />}
        title={title}
        backBtn={viewPath}
        breadcrumbs={<Page.Breadcrumbs items={breadcrumbItems} />}
      />
      <Page.Content>
        <ComposeEditForm composition={composition} station={station} />
      </Page.Content>
    </Page>
  );
}

/**
 * Loads composition detail then renders the edit form (same fields as create, name only).
 */
export function ComposeEditPage() {
  const { compositionId } = useParams<{ compositionId: string }>();
  const query = useCompositionQuery(compositionId!);

  return (
    <PageLoader skeleton={<PageSkeleton breadcrumbs={4} />} query={query}>
      <ComposeEditPageContent />
    </PageLoader>
  );
}
